import { injectable } from 'inversify';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type {
  IViewItemsViewModelController,
  ISelectableItem,
  IMeasurementViewModel,
  ICenterLineViewModel
} from './interfaces';
import { SceneItemKind } from './interfaces';
import type { ISelectionReactionStrategy } from './SelectionReaction.controller';
import type { ImagesService } from './modules/images/Images.service';
import type { ViewsService } from './Views.service';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { findNearestSegmentPoint } from '@/engine/utils/matSdkHelpers';
import { ImagesMode } from './modules/images/interfaces';
import { findNearestPolylinePointToPlane } from '@/engine/utils/geometryHelper';
import type * as THREE from 'three';
import type { ViewController } from './View.controller';

@injectable()
export class ViewModelSelectionReactionStrategiesProvider {
  private readonly _vmController: IViewItemsViewModelController;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  public constructor(
    vmController: IViewItemsViewModelController,
    viewsService: ViewsService,
    viewController: ViewController,
    imagesService: ImagesService,
    planningAlgorithm: PlanningAlgorithm
  ) {
    this._vmController = vmController;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
  }

  public getStrategy(kind: SceneItemKind): ISelectionReactionStrategy | undefined {
    switch (kind) {
      case SceneItemKind.SplineAreaMeasurement:
      case SceneItemKind.UnrestrictedSplineAreaMeasurement:
      case SceneItemKind.DiameterMeasurement:
      case SceneItemKind.AreaMeasurement: {
        return new AreaStrategy(
          this._vmController,
          this._viewsService,
          this._viewController,
          this._imagesService,
          this._planningAlgorithm
        );
      }
      case SceneItemKind.DistanceMeasurement:
      case SceneItemKind.LabeledDistanceMeasurement: {
        return new DistanceStrategy(this._vmController, this._imagesService, this._viewsService, this._viewController);
      }
      case SceneItemKind.AngleMeasurement: {
        return new AngleStrategy(this._vmController, this._imagesService, this._viewsService, this._viewController);
      }
      case SceneItemKind.IndicatedAnnotation:
      case SceneItemKind.IndicatedDistance:
      case SceneItemKind.IndicatedAngle:
      case SceneItemKind.IndicatedSplineMeasurement:
      case SceneItemKind.IndicatedAreaMeasurement: {
        return new IndicationLocationStrategy(this._vmController, this._imagesService, this._viewController);
      }
      case SceneItemKind.CuspPoint:
      case SceneItemKind.CommissurePoint: {
        return new PointStrategy(this._vmController, this._imagesService, this._viewController);
      }
      default: {
        return;
      }
    }
  }
}

class AreaStrategy implements ISelectionReactionStrategy {
  private _vmController: IViewItemsViewModelController;

  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _centerLines: Array<ICenterLineViewModel>;

  public constructor(
    vmController: IViewItemsViewModelController,
    viewsService: ViewsService,
    viewController: ViewController,
    imagesService: ImagesService,
    planningAlgorithm: PlanningAlgorithm
  ) {
    this._vmController = vmController;
    this._viewController = viewController;
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
    this._centerLines = viewsService.currentView?.getCenterLines() ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async reactOnSelection(viewModel: ISelectableItem): Promise<void> {
    const areaModel = await this._planningAlgorithm.getValueAsync(viewModel.descriptor, DT.AreaMeasurement);

    if (!areaModel) {
      console.warn('Area Measurement model is undefined.');
      return;
    }

    if (viewModel.imagesMode === ImagesMode.POLYLINE) {
      const centerLines = viewModel.clDescriptor
        ? this._centerLines.filter((cl) => cl.descriptor === viewModel.clDescriptor)
        : this._centerLines;

      const centerLineCurrentIfo = this._getThisAreaCenterLine(centerLines, areaModel);
      if (centerLineCurrentIfo?.clVM) {
        this._viewController.setActiveCenterLineDescriptor(centerLineCurrentIfo.clVM.descriptor);
        this._imagesService.requestSlice(
          centerLineCurrentIfo.clVM.alongSliceDescriptor,
          centerLineCurrentIfo.segmentIndex
        );
        this._imagesService.setImagesMode(ImagesMode.POLYLINE);
      } else {
        this._viewController.setActiveCenterLineDescriptor(undefined);
      }
    } else {
      this._viewController.setActiveCenterLineDescriptor(undefined);
    }

    this._vmController.locateMeasurementByDescriptor(viewModel.descriptor);
  }

  private _getThisAreaCenterLine(
    centerLines: Array<ICenterLineViewModel>,
    area: DT.AreaMeasurement
  ): { segmentIndex: number; clVM: ICenterLineViewModel } | undefined {
    if (centerLines.length === 0) return undefined;
    let clInfo = centerLines[0];
    let pts: Array<THREE.Vector3> = [];
    let minDist = -1;
    for (const cl of centerLines) {
      const clModel = this._planningAlgorithm.getValue(cl.descriptor, DT.Centerline);
      if (clModel === undefined) {
        console.warn('Centerline model is undefined');
        return undefined;
      }
      const found = findNearestSegmentPoint(clModel.value.points, area.center);
      if (minDist < 0 || found.distance < minDist) {
        clInfo = cl;
        pts = clModel.value.points;
        minDist = found.distance;
      }
    }
    const segmentIndex = findNearestPolylinePointToPlane(pts, area.normal, area.center);
    return {
      segmentIndex: segmentIndex,
      clVM: clInfo
    };
  }
}

class DistanceStrategy implements ISelectionReactionStrategy {
  private readonly _vmController: IViewItemsViewModelController;

  private readonly _imagesService: ImagesService;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  public constructor(
    vmController: IViewItemsViewModelController,
    imagesService: ImagesService,
    viewsService: ViewsService,
    viewController: ViewController
  ) {
    this._vmController = vmController;
    this._imagesService = imagesService;
    this._viewsService = viewsService;
    this._viewController = viewController;
  }

  public reactOnSelection(viewModel: ISelectableItem): void {
    if (viewModel.imagesMode === ImagesMode.MPR) {
      this._viewController.setActiveCenterLineDescriptor(undefined);
    } else {
      const centerLines = this._viewsService.currentView?.getCenterLines();
      if (centerLines?.length !== 1) {
        throw new Error("Can't get centerline of the distance");
      }
      this._viewController.setActiveCenterLineDescriptor(centerLines[0].descriptor);
    }
    this._imagesService.setImagesMode((viewModel as IMeasurementViewModel).imagesMode);
    this._vmController.locateMeasurementByDescriptor(viewModel.descriptor);
  }
}

class AngleStrategy implements ISelectionReactionStrategy {
  private _vmController: IViewItemsViewModelController;

  private readonly _imagesService: ImagesService;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  public constructor(
    vmController: IViewItemsViewModelController,
    imagesService: ImagesService,
    viewsService: ViewsService,
    viewController: ViewController
  ) {
    this._vmController = vmController;
    this._imagesService = imagesService;
    this._viewsService = viewsService;
    this._viewController = viewController;
  }

  public reactOnSelection(viewModel: ISelectableItem): void {
    if (viewModel.imagesMode === ImagesMode.MPR) {
      this._viewController.setActiveCenterLineDescriptor(undefined);
    } else {
      const centerLines = this._viewsService.currentView?.getCenterLines();
      if (centerLines?.length !== 1) {
        throw new Error("Can't get this angle's centerline");
      }
      this._viewController.setActiveCenterLineDescriptor(centerLines[0].descriptor);
    }
    this._imagesService.setImagesMode((viewModel as IMeasurementViewModel).imagesMode);
    this._vmController.locateMeasurementByDescriptor(viewModel.descriptor);
  }
}

class IndicationLocationStrategy implements ISelectionReactionStrategy {
  private readonly _vmController: IViewItemsViewModelController;

  private readonly _imagesService: ImagesService;

  private readonly _viewController: ViewController;

  public constructor(
    vmController: IViewItemsViewModelController,
    imagesService: ImagesService,
    viewController: ViewController
  ) {
    this._vmController = vmController;
    this._imagesService = imagesService;
    this._viewController = viewController;
  }

  public reactOnSelection(viewModel: ISelectableItem): void {
    if (this._imagesService.isReady()) {
      // TODO: MPR images mode should always deselect centerline!
      //   fix code duplications with:
      //     useImageModeHandlers.ts
      //     OrientationPresets.controller.ts
      //     cinelooControlController.ts
      //     viewModelSelectionReactionStrategiesProvider.ts
      this._viewController.setActiveCenterLineDescriptor(undefined);
      this._imagesService.setImagesMode(ImagesMode.MPR);

      this._vmController.locateIndicationByDescriptor(viewModel.descriptor);
    }
  }
}

export class PointStrategy implements ISelectionReactionStrategy {
  private _vmController: IViewItemsViewModelController;

  private readonly _imagesService: ImagesService;

  private readonly _viewController: ViewController;

  public constructor(
    vmController: IViewItemsViewModelController,
    imagesService: ImagesService,
    viewController: ViewController
  ) {
    this._imagesService = imagesService;
    this._vmController = vmController;
    this._viewController = viewController;
  }

  public reactOnSelection(viewModel: ISelectableItem): void {
    if (viewModel.imagesMode === ImagesMode.MPR) {
      this._viewController.setActiveCenterLineDescriptor(undefined);
      this._imagesService.setImagesMode(viewModel.imagesMode);
    }

    this._vmController.locatePointByDescriptor(viewModel.descriptor);
  }
}
